import { getConfigsDistributedInPlacesInLayout } from 'common/dynamic-form/utils/get-configs-distributed-in-layout';
import { getGroupRowGap } from 'common/dynamic-form/utils/get-group-indent';
import { filterNotDefinedItems } from 'utils/filter-not-defined-items';

import { LayoutConfig } from './model/layout-config.interface';
import { LayoutProps } from './model/layout-props.interface';

import {
  DynamicFormLayoutPlace,
  ISPFieldConfig,
  ISPFieldType,
  ISPFieldTypeLayout,
  ISPFormState,
} from '../../model';
import { getGroupConfig } from '../group/group.utils';
import { GroupConfig } from '../group/model';
import { TemplateConfig } from '../template/model';

export const HEADER_GROUP_NAME = 'header';
export const CONTENT_GROUP_NAME = 'content';
export const FOOTER_GROUP_NAME = 'footer';
export const SUMMARY_GROUP_NAME = 'summary';
export const ASIDE_GROUP_NAME = 'aside';
export const UNDER_FORM_GROUP_NAME = 'underForm';
export const PAGE_FOOTER_GROUP_NAME = 'pageFooter';

/**
 * Get a group of fields to display in a specific layout location
 *
 * @param place - place, where fields group will be inserted for display in layout
 * @param configs
 */
export function getFieldGroupByPlace(
  place: DynamicFormLayoutPlace,
  configs: ISPFieldConfig[],
): GroupConfig | TemplateConfig | undefined {
  return configs.find(config => place === config.props.name) as
    | GroupConfig
    | TemplateConfig
    | undefined;
}

/**
 * Get props for layout field
 *
 * @param state - form state
 * @param configs
 */
export function getLayoutProps(
  state: ISPFormState,
  configs: ISPFieldConfig[],
): LayoutProps {
  const header = getFieldGroupByPlace(HEADER_GROUP_NAME, configs);
  if (header) {
    // header block always have small indents
    header.props.rowGap = getGroupRowGap('small');
  }

  const content = getFieldGroupByPlace(CONTENT_GROUP_NAME, configs);
  if (content && state.docSubject.value.metadata?.form?.$indent) {
    content.props.rowGap = getGroupRowGap(
      state.docSubject.value.metadata.form.$indent,
    );
  }

  const aside = getFieldGroupByPlace(ASIDE_GROUP_NAME, configs);
  if (aside) {
    // aside block always have small indents
    aside.props.rowGap = getGroupRowGap('small');
  }

  return {
    displayBackground:
      state.docSubject.value.metadata?.form?.$nobackground !== 'yes',
  };
}

/**
 * Get layout type according to BE metadata
 *
 * @param state
 */
function getLayoutType(state: ISPFormState): ISPFieldTypeLayout {
  switch (true) {
    case state.docSubject.value.metadata?.form?.$type === 'mini':
      return ISPFieldType.MiniLayout;
    case state.docSubject.value.metadata?.form?.$type === 'card':
      return ISPFieldType.CardLayout;
    case state.isExtformLayout:
      return ISPFieldType.ExtformLayout;
    default:
      return ISPFieldType.DefaultLayout;
  }
}

/**
 * Get config for layout field
 *
 * @param configs - form fields
 * @param state - form state
 */
export function getLayoutConfig(
  configs: ISPFieldConfig[],
  state: ISPFormState,
): LayoutConfig {
  const configsByPlaces = getConfigsDistributedInPlacesInLayout(configs);

  const groupsByPlace: Record<
    Exclude<DynamicFormLayoutPlace, 'absolute'>,
    GroupConfig | undefined
  > = {
    aside: getGroupConfig(configsByPlaces.aside, ASIDE_GROUP_NAME),
    header: getGroupConfig(configsByPlaces.header, HEADER_GROUP_NAME),
    content: getGroupConfig(configsByPlaces.content, CONTENT_GROUP_NAME),
    footer: getGroupConfig(configsByPlaces.footer, FOOTER_GROUP_NAME),
    summary: getGroupConfig(configsByPlaces.summary, SUMMARY_GROUP_NAME),
    underForm: getGroupConfig(configsByPlaces.underForm, UNDER_FORM_GROUP_NAME),
    pageFooter: getGroupConfig(
      configsByPlaces.pageFooter,
      PAGE_FOOTER_GROUP_NAME,
    ),
  };

  const fieldGroup = filterNotDefinedItems([
    groupsByPlace.aside,
    groupsByPlace.header,
    groupsByPlace.content,
    groupsByPlace.footer,
    groupsByPlace.summary,
    groupsByPlace.underForm,
    groupsByPlace.pageFooter,
    ...configsByPlaces.absolute,
  ]);

  const props = getLayoutProps(state, fieldGroup);

  const config: LayoutConfig = {
    type: getLayoutType(state),
    props,
    fieldGroup,
  };

  return config;
}
